import React, { useState, useEffect } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "./RagData.css"; // Import the custom CSS
import Navbar from "../../components/Navbar/Navbar";
import CustomHeader from "../../components/CustomHeader/CustomHeader";
import CustomerTicketsModal from "../../components/CustomerTicketsModal/CustomerTicketsModal";
import IssueSummaryModal from "../../components/IssueSummaryModal/IssueSummaryModal"; // Import the new modal

const RagList = () => {
  const [ragData, setRagData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [ticketDetails, setTicketDetails] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIssueSummaryModalOpen, setIsIssueSummaryModalOpen] = useState(false);
  const [currentIssueSummary, setCurrentIssueSummary] = useState(null);

  useEffect(() => {
    fetchRagData();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchCustomerTickets(selectedCustomerId);
    }
  }, [selectedCustomerId]);

  const fetchRagData = async () => {
    try {
      const response = await axios.get("/rag-data");
      setRagData(response.data);
    } catch (error) {
      console.error("Error fetching RAG data:", error);
      setError("Failed to fetch RAG data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerTickets = async (customerId) => {
    try {
      const response = await axios.get(`/customers/${customerId}/tickets`);
      setTicketDetails(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching customer tickets:", error);
      setError("Failed to fetch customer tickets. Please try again later.");
    }
  };

  const redirectToAccount = async (customerId) => {
    try {
      const response = await axios.get(`/customers/${customerId}/tickets`);
      if (customerId) {
        const url = `https://ameyo.lightning.force.com/lightning/r/Account/${customerId}/view`;
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error redirecting to account:", error);
      setError("Failed to redirect to account. Please try again later.");
    }
  };

  const handleOpenIssueDetails = (issuesSummary) => {
    try {
      // Remove the outer quotes if they exist
      let processedSummary = issuesSummary;

      if (processedSummary.startsWith('"') && processedSummary.endsWith('"')) {
        processedSummary = processedSummary.slice(1, -1);
      }

      // Unescape the JSON string
      const unescapedIssuesSummary = processedSummary.replace(/\\"/g, '"');

      // Ensure the string ends with '}' for proper JSON parsing
      let fixedSummary = unescapedIssuesSummary;
      if (!unescapedIssuesSummary.endsWith("}")) {
        fixedSummary = `${unescapedIssuesSummary}}`;
      }

      // Parse the potentially fixed summary
      const issuesSummaryArray = JSON.parse(`[${fixedSummary}]`);

      console.log("Issues Summary:", issuesSummaryArray);
      setCurrentIssueSummary(issuesSummaryArray);
      setIsIssueSummaryModalOpen(true);
    } catch (error) {
      console.error("Error processing issue summary:", error);
      setError("Failed to process issue summary. Please try again later.");
    }
  };

  const handleCellClick = (event) => {
    const customerId = event.data.customer_id;

    if (event.column.colId === "customer_id") {
      redirectToAccount(customerId);
    } else if (event.column.colId === "customer_name") {
      setSelectedCustomerId(customerId);
      setIsModalOpen(true);
    } else {
      return;
    }
  };

  const columnDefs = [
    {
      headerName: "Customer ID",
      field: "customer_id",
      sortable: true,
      filter: true,
      resizable: true,
      width: 200,
      headerComponentFramework: CustomHeader,
      cellStyle: { textAlign: "left", cursor: "pointer", color: "blue" },
    },
    {
      headerName: "Customer Name",
      field: "customer_name",
      sortable: true,
      filter: true,
      resizable: true,
      width: 300,
      headerComponentFramework: CustomHeader,
      cellStyle: { textAlign: "left", cursor: "pointer", color: "blue" },
    },
    {
      headerName: "Issue Summary",
      field: "issue_summary",
      width: 150,
      cellRenderer: (params) => {
        return (
          <button
            className="text-sm bg-green-500 rounded-md py-2 px-2"
            onClick={() => handleOpenIssueDetails(params.data.issues_summary)}
          >
            View Details
          </button>
        );
      },
    },
    {
      headerName: "Average CSAT Score",
      field: "avg_csat_score",
      sortable: true,
      filter: true,
      resizable: true,
      width: 200,
      headerComponentFramework: CustomHeader,
      cellStyle: { textAlign: "left" },
    },
    {
      headerName: "RAG Status",
      field: "rag_status",
      sortable: true,
      filter: true,
      resizable: true,
      width: 150,
      headerComponentFramework: CustomHeader,
      cellStyle: (params) => {
        const base = { textAlign: "center", color: "#fff", fontWeight: "600", borderRadius: "4px" };
        switch (params.value) {
          case "Red":
            return { ...base, backgroundColor: "#e53e3e" };
          case "Amber":
            return { ...base, backgroundColor: "#dd6b20", color: "#fff" };
          case "Green":
            return { ...base, backgroundColor: "#38a169" };
          default:
            return { textAlign: "left" };
        }
      },
    },
  ];

  return (
    <div>
      <Navbar />
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-2xl font-bold mb-3">Customer RAG Analysis</h1>
        {loading && <p className="text-gray-600">Loading...</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && (
          <>
            <div
              className="ag-theme-quartz"
              style={{ height: 520, width: "100%" }}
            >
              <AgGridReact
                rowData={ragData}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={10}
                defaultColDef={{
                  minWidth: 100,
                  resizable: true,
                  sortable: true,
                  filter: true,
                }}
                onCellClicked={handleCellClick}
              />
            </div>
            {isModalOpen && (
              <CustomerTicketsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                ticketDetails={ticketDetails}
              />
            )}
            {isIssueSummaryModalOpen && (
              <IssueSummaryModal
                isOpen={isIssueSummaryModalOpen}
                onClose={() => setIsIssueSummaryModalOpen(false)}
                issueSummary={currentIssueSummary}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RagList;
